/**
 * Guardrails for radio icons and playback URLs.
 */

import { withMediaAccessToken, needsMediaAccessToken } from './s3MediaAuth';
import { getRadioStreamUrl } from '../content/namespaces/domain.js';

const ICON_PATH_PREFIX = '/data/gallery/studio/radio/icons/';

export function isCachedRadioIconUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return false;
  if (raw.startsWith(ICON_PATH_PREFIX)) return true;
  try {
    if (typeof window !== 'undefined') {
      const parsed = new URL(raw, window.location.origin);
      return parsed.pathname.startsWith(ICON_PATH_PREFIX);
    }
  } catch {
    return false;
  }
  return false;
}

export function sanitizeRadioIconUrl(url) {
  return isCachedRadioIconUrl(url) ? String(url).trim() : '';
}

export function streamHostFromUrl(url) {
  try {
    return new URL(String(url || '').trim()).hostname.replace(/^www\./i, '') || '';
  } catch {
    return '';
  }
}

export function isHlsStreamUrl(url) {
  return /\.m3u8(\?|$)/i.test(String(url || ''));
}

export function radioStreamProxyUrls(rawUrl) {
  const raw = String(rawUrl || '').trim();
  if (!raw) return [];
  const apiBase = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
  const encoded = encodeURIComponent(raw);
  return [
    `${apiBase}/radio-stream?url=${encoded}`,
    `${apiBase}/radio-browser/stream?url=${encoded}`,
  ];
}

export function resolveRadioPlaybackUrl(station) {
  const raw = String(station?.url || '').trim();
  if (!raw) return '';

  if (station?.playbackUrl) return station.playbackUrl;

  // Member media (musicians, collection) — same-origin S3 proxy, not icecast proxy.
  if (station?.kind === 'track' || needsMediaAccessToken(raw)) {
    return withMediaAccessToken(raw);
  }

  // HLS: manifest + segments are proxied via hls.js xhrSetup (see studioRadioPlayback.js).
  if (isHlsStreamUrl(raw)) return getRadioStreamUrl(raw) || proxiedStreamUrl(raw);

  return radioStreamProxyUrls(raw)[0] || '';
}

function proxiedStreamUrl(rawUrl) {
  const apiBase = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
  return `${apiBase}/radio-stream?url=${encodeURIComponent(String(rawUrl || '').trim())}`;
}

export function mediaErrorMessage(code) {
  switch (code) {
    case MediaError.MEDIA_ERR_ABORTED:
      return 'Playback was interrupted.';
    case MediaError.MEDIA_ERR_NETWORK:
      return 'Network error — check your connection or try another station.';
    case MediaError.MEDIA_ERR_DECODE:
      return 'This stream format is not supported in your browser.';
    case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
      return 'Stream unavailable (host offline, blocked, or bad certificate).';
    default:
      return 'Playback failed. Try another station.';
  }
}

export function classifyPlaybackFailure(err, audioEl) {
  const code = audioEl?.error?.code;
  if (code) return mediaErrorMessage(code);
  const name = String(err?.name || '').trim();
  if (name === 'NotAllowedError') {
    return 'Tap play again to start audio (browser blocked autoplay).';
  }
  const msg = String(err?.message || err || '').trim();
  if (/timeout/i.test(msg)) return 'Connection timed out — station may be offline.';
  if (/HLS/i.test(msg)) return 'HLS stream failed — try another station.';
  if (/security policy/i.test(msg)) return 'Stream blocked by browser security policy.';
  if (/certificate|SSL|TLS|CERT/i.test(msg)) return 'Stream host has an invalid certificate.';
  return msg || 'Playback failed. Try another station.';
}

export function waitForAudioCanPlay(audioEl, timeoutMs = 15000) {
  return new Promise((resolve, reject) => {
    if (!audioEl) {
      reject(new Error('Audio element required'));
      return;
    }
    const isReady = () =>
      audioEl.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA ||
      (audioEl.readyState >= HTMLMediaElement.HAVE_METADATA && Number.isFinite(audioEl.duration));
    if (isReady()) {
      resolve();
      return;
    }

    let timer;
    const cleanup = () => {
      clearTimeout(timer);
      audioEl.removeEventListener('canplay', onReady);
      audioEl.removeEventListener('loadeddata', onReady);
      audioEl.removeEventListener('loadedmetadata', onReady);
      audioEl.removeEventListener('error', onError);
    };
    const onReady = () => {
      if (audioEl.readyState >= HTMLMediaElement.HAVE_METADATA) {
        cleanup();
        resolve();
      }
    };
    const onError = () => {
      cleanup();
      reject(new Error(classifyPlaybackFailure(null, audioEl)));
    };

    audioEl.addEventListener('canplay', onReady);
    audioEl.addEventListener('loadeddata', onReady);
    audioEl.addEventListener('loadedmetadata', onReady);
    audioEl.addEventListener('error', onError);
    timer = setTimeout(() => {
      cleanup();
      if (audioEl.readyState >= HTMLMediaElement.HAVE_METADATA && !audioEl.error) {
        resolve();
        return;
      }
      reject(new Error('Connection timed out — station may be offline.'));
    }, timeoutMs);
  });
}
